import { Http } from "@jelly/lesson";
import config from "appConfig";
import ky from "ky";

export interface AuthAccessToken {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: unknown;
  username: string;
}

export default class AuthProvider {
  http: Http;

  constructor(http: Http) {
    this.http = http;
    const authToken = this.getAuthStore();
    const landingPageUrl = config.LANDING_PAGE_URL ?? "/";

    if (authToken) {
      http.addHeader("Authorization", `Bearer ${authToken.access_token}`);
    } else {
      location.href = landingPageUrl;
    }

    http.extend({
      hooks: {
        afterResponse: [
          async (request, options, response) => {
            if (response.status === 401) {
              const authToken = this.getAuthStore();
              if (!authToken) {
                location.href = landingPageUrl;
                return;
              }

              // Logout if user attempted to refresh token and still got 401
              if (request.headers.has("X-Attempted-Refresh")) {
                request.headers.delete("X-Attempted-Refresh");
                this.logout();
                return;
              }

              try {
                const refreshedToken = await this.refreshToken(authToken.refresh_token);
                localStorage.setItem("auth", JSON.stringify(refreshedToken));

                http.addHeader("Authorization", `Bearer ${refreshedToken.access_token}`);
                request.headers.set("Authorization", `Bearer ${refreshedToken.access_token}`);
                request.headers.set("X-Attempted-Refresh", "true");
                return ky(request);
              } catch (refreshError) {
                this.logout();
              }
              return Promise.reject("Something went wrong");
            }
          },
        ],
      },
    });
  }

  getAuthStore(): AuthAccessToken | null {
    if (process.env.NODE_ENV === "development") {
      return {
        access_token: "dev",
        refresh_token: "dev",
        expires_in: 100000,
        token_type: "Bearer",
        scope: "dev",
        username: "dev",
      };
    }

    const authStore = localStorage.getItem("auth");
    return authStore !== null ? JSON.parse(authStore) : null;
  }

  logout(): void {
    localStorage.removeItem("auth");

    const lmsLandingPagePath = config.LMS_LANDING_PAGE_PATH;
    if (!lmsLandingPagePath) {
      return;
    }

    location.href = lmsLandingPagePath;
  }

  refreshToken(refreshToken: string): Promise<AuthAccessToken> {
    const formData = new FormData();
    formData.append("grant_type", "refresh_token");
    formData.append("client_id", config.API_CLIENT_ID);
    formData.append("client_secret", config.API_CLIENT_SECRET);
    formData.append("refresh_token", refreshToken);

    return ky
      .post(`${config.API_URL}/oauth/v2/token`, {
        body: formData,
      })
      .then((response) => {
        return response.json();
      });
  }
}
